// @flow
import ResourceModel from "../base/ResourceModel";
import UserModel from "../user/UserModel";
import ErrorResponse from "../error/ErrorResponse";
import Cache from "../../utils/browser/Cache";

import type { ModularUIModel } from "../types";
import type { ModularUIResponse } from "../../modularui";
import type LinkModel from "../links/LinkModel";
import type Href from "../href/Href";

/**
 * UserServicesModel model
 */
class UserServicesModel extends ResourceModel {
  _user: ?UserModel;
  _mustChangePassword: boolean = false;

  /**
   */
  get type(): string {
    return "UserServices";
  }

  /**
   */
  static get modelName(): string {
    return "UserServicesModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    return (
      data.contributions.resourcetype &&
      data.contributions.resourcetype === "user_services"
    );
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    const userData = this.userLink;
    return userData ? [userData] : [];
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>, errors: Array<ErrorResponse>) {
    this.mustChangePassword = Array.isArray(errors)
      ? (errors.some((error) => error.isChangePassword) ?? false)
      : false;
    this.user = models.find((model) => model.type === "User");
  }

  /**
   */
  get userLink(): LinkModel | null {
    return this.links.getLinkByKey("Userdata");
  }

  /**
   * return the user model for the current user
   */
  get user(): UserModel | null {
    return this._user || null;
  }

  /**
   * Set user data
   */
  set user(model: ?ModularUIModel) {
    this._user = model instanceof UserModel ? model : null;
  }

  /**
   * Getting the label of the application
   */
  get label(): string {
    return this.getContribution("label", "");
  }

  /**
   */
  get changePassword(): Href | null {
    const changePasswordLink = this.links.getLinkByKey("ChangePassword");

    if (changePasswordLink) {
      return changePasswordLink.href;
    }

    return null;
  }

  /**
   */
  get changeAvatar(): Href | null {
    const changeAvatarLink = this.links.getLinkByKey("ChangeAvatar");

    if (changeAvatarLink) {
      return changeAvatarLink.href;
    }

    return null;
  }

  // when more than three unsecure links are present (always present: self, contributions and api-docs),
  // the userservice is permitted and thus we have an indication the user is probably
  // logged in
  /**
   */
  get isLoggedIn(): boolean {
    const hasUser = this.user instanceof UserModel;

    const UNSECURE_LINK_COUNT = 3;
    return (
      this.links.length > UNSECURE_LINK_COUNT ||
      hasUser ||
      Cache.getItem("basic-ok") === true
    );
  }

  /**
   */
  set mustChangePassword(mustChangePassword: boolean) {
    this._mustChangePassword = mustChangePassword;
  }

  /**
   */
  get mustChangePassword(): boolean {
    return this._mustChangePassword;
  }
}

export default UserServicesModel;
